import {
    Box,
    Button,
    Checkbox,
    Heading,
    Link,
    Modal,
    Spacer,
    SuccessAnimation,
    Text,
    TextArea,
} from '@ksyos/design-system';
import { useLocation } from '@ksyos/router';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '../../configureStore';
import { fetchJson } from '../../store/fetch';
import { translateKey } from '../../translate';

interface IFeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
    url: string;
}

interface IEmojiData {
    code: string;
    name: string;
    opacity: string;
    value: number;
}

const emojiData = [
    { code: '😣', name: 'persevering-face', value: 0, opacity: '100%' },
    { code: '🙁', name: 'slightly-frowning-face', value: 1, opacity: '100%' },
    { code: '😐', name: 'neutral-face', value: 2, opacity: '100%' },
    { code: '😁', name: 'smiling-face-with-open-mouth-and-smiling-eyes', value: 3, opacity: '100%' },
    { code: '❤️', name: 'red-heart', value: 4, opacity: '100%' },
];

export function FeedbackModal(props: IFeedbackModalProps) {
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [feedbackText, setFeedbackText] = useState<string>('');
    const [contactPractitioner, setContactPractitioner] = useState<boolean>(false);
    const [emojis, setEmojis] = useState<IEmojiData[]>(emojiData);
    const [selectedEmojiValue, setSelectedEmojiValue] = useState<number>();

    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const { pathname } = useLocation();

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => {
                props.onClose();
            }}
            onCloseComplete={() => setShowSuccess(false)}
            size="small"
        >
            {!showSuccess && (
                <>
                    <Modal.Header>{translateKey('support.giveFeedback')}</Modal.Header>
                    <Modal.CloseButton />
                    <Modal.Body>
                        <Text
                            fontWeight="medium"
                            mb={2}
                            size="sm"
                        >
                            {translateKey('support.rateQuestion')}
                        </Text>
                        <Box mb={6}>
                            <Spacer
                                direction="horizontal"
                                spacing="medium"
                            >
                                {emojis.map((emoji) => (
                                    <Button
                                        key={emoji.code}
                                        onClick={() => handleEmojiClick(emoji.value)}
                                        style={{ fontSize: '32px', textDecoration: 'none' }}
                                        variant="link"
                                    >
                                        <span
                                            aria-labelledby={emoji.name}
                                            role="img"
                                            style={{ opacity: emoji.opacity }}
                                        >
                                            {emoji.code}
                                        </span>
                                    </Button>
                                ))}
                            </Spacer>
                        </Box>

                        <Text
                            fontWeight="medium"
                            mb={2}
                            size="sm"
                        >
                            {translateKey('support.anythingElse')}
                        </Text>
                        <TextArea
                            maxLength={255}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Typ je opmerking"
                            rows={4}
                            value={feedbackText}
                            variant="outline"
                        />

                        <Checkbox
                            checked={contactPractitioner}
                            label={<Text as="span">Ksyos mag bij eventuele vragen contact opnemen</Text>}
                            onChange={(event) => setContactPractitioner(event.target.checked)}
                        />

                        <Text
                            color="minimal"
                            ml={8}
                            mt={1}
                            size="sm"
                        >
                            {'We gaan vertrouwelijk om met je gegevens (zie '}
                            <Link
                                fontWeight="normal"
                                href="https://ksyos.nl/privacy-informatiebeveiliging/"
                                openInNewTab
                                variant="externalLink"
                            >
                                privacy
                            </Link>
                            {')'}
                        </Text>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button
                            isDisabled={selectedEmojiValue === undefined}
                            onClick={async () => await handleSubmit()}
                            width="full"
                        >
                            {translateKey('support.submit')}
                        </Button>
                    </Modal.Footer>
                    <Modal.Footnote>
                        <Text size="md">
                            Hulp nodig? Bel dan met <b>020 - 600 0060</b>
                        </Text>
                    </Modal.Footnote>
                </>
            )}
            {showSuccess && (
                <>
                    <Modal.Body>
                        <Box
                            display="flex"
                            justifyContent="center"
                            mt={4}
                        >
                            <SuccessAnimation size="large" />
                        </Box>
                        <Heading
                            mb={2}
                            size="large"
                            textAlign="center"
                        >
                            {translateKey('support.thanks')}
                        </Heading>
                        <Text textAlign="center">{translateKey('support.happyForFeedback')}</Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            onClick={() => {
                                setShowSuccess(false);
                                props.onClose();
                            }}
                            width="full"
                        >
                            {translateKey('common.close')}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );

    function handleEmojiClick(value: number) {
        setEmojis(emojiData.map((emoji) => (emoji.value === value ? emoji : { ...emoji, opacity: '40%' })));
        setSelectedEmojiValue(value);
    }

    async function handleSubmit() {
        await fetchJson('/api/feedback', 'POST', {
            text: feedbackText,
            value: selectedEmojiValue,
            practitionerId: contactPractitioner ? practitionerId : null,
            url: pathname,
        });
        setShowSuccess(true);
        setFeedbackText('');
        setSelectedEmojiValue(undefined);
        setContactPractitioner(false);
        setEmojis(emojiData);
    }
}
